import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import throttle from 'lodash/throttle';

import { viewportChanged, initialized } from '../../../store';
import { round } from '../../../../common/utils/helpers/math';
import { useGlMap } from '../../../contexts/context';
import { executeWhenMapLoaded } from '../../../utils/helpers/utils';

const ACTIONS_DEBOUNCE_WAIT_MS = 50;

export interface ReduxControlProps {
  centerPrecision?: number;
  zoomPrecision?: number;
}

const ReduxControl = ({
  centerPrecision = 5,
  zoomPrecision = 3,
}: ReduxControlProps) => {
  const glMap = useGlMap();
  const dispatch = useDispatch();

  // sync from map to redux store
  useEffect(() => {
    if (!glMap) {
      return;
    }

    const getMapState = (glMap) => ({
      zoom: round(glMap.getZoom(), zoomPrecision),
      center: glMap.getCenter().toArray().map(round.fp(centerPrecision)),
    });
    const dispatchViewportChanged = throttle(() => {
      dispatch(viewportChanged(getMapState(glMap)));
    }, ACTIONS_DEBOUNCE_WAIT_MS);

    const dispatchInitialized = () => {
      dispatch(initialized(getMapState(glMap)));
    };

    glMap.on('move', dispatchViewportChanged);
    glMap.on('zoom', dispatchViewportChanged);

    executeWhenMapLoaded(glMap, dispatchInitialized);

    return () => {
      dispatchViewportChanged.cancel();
      glMap.off('move', dispatchViewportChanged);
      glMap.off('zoom', dispatchViewportChanged);
    };
  }, [dispatch, glMap, zoomPrecision, centerPrecision]);

  return null;
};

export default ReduxControl;
